import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Select, MenuItem, FormControl, InputLabel, TextField, CircularProgress, Typography,
} from '@mui/material';
import { api } from '../../api/api';

// 감사 로그 (services/audit.js) — 누가 언제 어느 기기·매장·사용자에 무엇을 했는지
const LABELS = {
  DEVICE_COMMAND: ['기기 명령', '#551FFF'], DEVICE_BROADCAST: ['전체 명령', '#7c3aed'],
  DEVICE_TICKER: ['자막', '#0ea5e9'], DEVICE_CLEAR_CACHE: ['캐시 초기화', '#f59e0b'],
  APK_UPLOAD: ['APK 업로드', '#16a34a'], APK_UPDATE: ['APK 배포 변경', '#16a34a'], APK_ADD: ['APK 등록', '#16a34a'],
  STORE_UPDATE: ['매장 수정', '#3b82f6'], REQUEST_UPDATE: ['요청 상태', '#64748b'],
  USER_APPROVE: ['가입 승인', '#22c55e'], USER_REJECT: ['가입 거절', '#ef4444'], USER_ROLE: ['역할 변경', '#ef4444'],
  FIELD_CODE_ISSUE: ['현장 코드 발급', '#db2777'], FIELD_CODE_REVOKE: ['현장 코드 취소', '#db2777'],
};
const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const metaText = (m) => {
  if (!m || typeof m !== 'object') return '';
  return Object.entries(m).filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ');
};

export default function AuditTable() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [target, setTarget] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: '300' });
      if (action) q.set('action', action);
      if (target.trim()) q.set('targetId', target.trim());
      const res = await api.get(`/audit?${q}`);
      setLogs(res.data.logs || []);
    } catch (_) { setLogs([]); }
    setLoading(false);
  }, [action, target]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>작업</InputLabel>
          <Select value={action} label="작업" onChange={(e) => setAction(e.target.value)}>
            <MenuItem value="">전체</MenuItem>
            {Object.entries(LABELS).map(([k, [l]]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" placeholder="대상 (기기 코드·ID)" value={target} onChange={(e) => setTarget(e.target.value)} sx={{ width: 200 }} />
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>1년 보관 · 최근 300건</Typography>
      </Box>
      {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{ background: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>시각</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>누가</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>작업</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>대상</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>내용</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>기록 없음</TableCell></TableRow>}
              {logs.map((l) => {
                const [label, color] = LABELS[l.action] || [l.action, '#64748b'];
                return (
                  <TableRow key={l._id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(l.at)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.actorRole || '—'} <Typography component="span" variant="caption" color="text.secondary">{String(l.actorId || '').slice(-6)}</Typography></TableCell>
                    <TableCell><Chip size="small" label={label} sx={{ bgcolor: `${color}1a`, color, fontWeight: 600 }} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.targetType ? `${l.targetType} ` : ''}<b>{l.targetId || ''}</b></TableCell>
                    <TableCell sx={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }} title={metaText(l.meta)}>{metaText(l.meta) || '—'}{l.ip ? ` · ${l.ip}` : ''}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
